import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  LoaderCircle,
  Save,
} from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import ImageUploadGuidance from "../common/ImageUploadGuidance";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import { useAuthStore } from "../../context/AuthContext";
import { hasPermission } from "../../lib/utils/permissions";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";

const EMPTY_FORM = {
  title: "",
  year: "",
  display_order: "0",
  photo: null,
  attachment: null,
};

const ALLOWED_PDF_TYPES = ["application/pdf"];
const NEWSLETTER_UPLOAD_LIMIT_MB = 150;
const NEWSLETTER_UPLOAD_LIMIT_BYTES =
  NEWSLETTER_UPLOAD_LIMIT_MB * 1024 * 1024;
const NEWSLETTER_UPLOAD_TIMEOUT_MS = 300000;

const NewsletterFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { newsletterId } = useParams();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState("");
  const [existingAttachment, setExistingAttachment] = useState(null);

  const isEditMode = mode === "edit";
  const isAdmin = hasPermission(user, "newsletters", isEditMode ? "edit" : "create");

  const pageTitle = useMemo(
    () => (isEditMode ? "Edit Newsletter" : "Create Newsletter"),
    [isEditMode],
  );

  useEffect(() => {
    if (!isEditMode || !isAdmin) {
      setIsPageLoading(false);
      return;
    }

    const fetchNewsletter = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/newsletters/${newsletterId}`);
        const newsletter = response.data?.data;

        if (!newsletter) {
          showErrorToast("Newsletter not found");
          navigate("/newsletters");
          return;
        }

        setForm({
          title: newsletter.title || "",
          year: newsletter.year ? String(newsletter.year) : "",
          display_order: String(newsletter.display_order ?? 0),
          photo: null,
          attachment: null,
        });
        setExistingPhotoUrl(newsletter.photo_url || "");
        setExistingAttachment({
          url: newsletter.attachment_url,
          type: newsletter.attachment_type,
        });
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load newsletter");
        navigate("/newsletters");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchNewsletter();
  }, [newsletterId, isAdmin, isEditMode, navigate, showErrorToast]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setFormErrors((current) => ({
      ...current,
      [field]: "",
    }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.title.trim()) nextErrors.title = "Newsletter title is required.";
    if (!form.year.trim()) {
      nextErrors.year = "Year is required.";
    } else if (!/^\d{4}$/.test(form.year.trim())) {
      nextErrors.year = "Enter a valid 4-digit year.";
    }
    if (!/^\d+$/.test(form.display_order.trim())) {
      nextErrors.display_order = "Enter a non-negative whole number.";
    }
    if (!isEditMode && !form.photo) {
      nextErrors.photo = "Photo is required.";
    }
    if (!isEditMode && !form.attachment) {
      nextErrors.attachment = "Attachment is required.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append("title", form.title.trim());
      payload.append("year", form.year.trim());
      payload.append("display_order", form.display_order.trim());

      if (form.photo) {
        payload.append("photo", form.photo);
      }

      if (form.attachment) {
        payload.append("attachment", form.attachment);
      }

      if (isEditMode) {
        await apiClient.put(`/newsletters/${newsletterId}`, payload, {
          timeout: NEWSLETTER_UPLOAD_TIMEOUT_MS,
        });
        showSuccessToast("Newsletter updated successfully");
      } else {
        await apiClient.post("/newsletters", payload, {
          timeout: NEWSLETTER_UPLOAD_TIMEOUT_MS,
        });
        showSuccessToast("Newsletter created successfully");
      }

      navigate("/newsletters");
    } catch (err) {
      showErrorToast(
        err.response?.data?.message ||
          (err.code === "ECONNABORTED"
            ? "Newsletter upload timed out. Please try again with a smaller file or a faster connection."
            : err.message || "Unable to save newsletter"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
        <p className="mt-2 text-sm text-slate-500">
          Newsletter management is only available for admin users.
        </p>
      </div>
    );
  }

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading newsletter editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Button
            type="button"
            variant="ghost"
            className="-ml-3 mb-2 rounded-lg px-3 text-slate-500"
            onClick={() => navigate("/newsletters")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Newsletters
          </Button>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            {pageTitle}
          </h1>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Manage newsletter title, photo, attachment, and year.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSave}
        className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl"
      >
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Title</label>
              <Input
                value={form.title}
                onChange={(event) => handleInputChange("title", event.target.value)}
                placeholder="Jan to June 2025"
                className={`rounded-lg ${formErrors.title ? "border-red-300 focus-visible:ring-red-500" : ""}`}
              />
              <FieldError>{formErrors.title}</FieldError>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Year</label>
              <Input
                value={form.year}
                onChange={(event) => handleInputChange("year", event.target.value)}
                placeholder="2026"
                className={`rounded-lg ${formErrors.year ? "border-red-300 focus-visible:ring-red-500" : ""}`}
              />
              <FieldError>{formErrors.year}</FieldError>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">
                Display order within year
              </label>
              <Input
                type="number"
                min="0"
                step="1"
                value={form.display_order}
                onChange={(event) => handleInputChange("display_order", event.target.value)}
                placeholder="1"
                className={`rounded-lg ${formErrors.display_order ? "border-red-300 focus-visible:ring-red-500" : ""}`}
              />
              <p className="text-xs text-slate-500">
                Lower numbers appear first for newsletters in the same year.
              </p>
              <FieldError>{formErrors.display_order}</FieldError>
            </div>

            <div className="space-y-2">
              <InlineFormFileUpload
                label="Photo"
                value={form.photo}
                onChange={(file) => handleInputChange("photo", file)}
                existingFiles={existingPhotoUrl ? [existingPhotoUrl] : []}
                accept={IMAGE_TYPES}
                maxSizeBytes={NEWSLETTER_UPLOAD_LIMIT_BYTES}
                required={!isEditMode}
                error={formErrors.photo}
                helperText={`JPG, PNG, GIF or WEBP — maximum ${NEWSLETTER_UPLOAD_LIMIT_MB} MB`}
              />
              <ImageUploadGuidance requirementKey="newsletterCover" file={form.photo} src={existingPhotoUrl} />
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <InlineFormFileUpload
                label="Attachment"
                value={form.attachment}
                onChange={(file) => handleInputChange("attachment", file)}
                existingFiles={existingAttachment?.url ? [{
                  name: existingAttachment.type === "pdf" ? "Current newsletter.pdf" : "Current newsletter image",
                  url: existingAttachment.url,
                  mimeType: existingAttachment.type === "pdf" ? "application/pdf" : "image/jpeg",
                }] : []}
                accept={["application/pdf", ...IMAGE_TYPES]}
                maxSizeBytes={NEWSLETTER_UPLOAD_LIMIT_BYTES}
                required={!isEditMode}
                error={formErrors.attachment}
                helperText={`PDF, JPG, PNG, GIF or WEBP — maximum ${NEWSLETTER_UPLOAD_LIMIT_MB} MB`}
              />
              <ImageUploadGuidance
                requirementKey="newsletterAttachment"
                file={form.attachment && !ALLOWED_PDF_TYPES.includes(form.attachment.type) ? form.attachment : null}
                src={!form.attachment && existingAttachment?.type !== "pdf" ? existingAttachment?.url : ""}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="rounded-lg"
            onClick={() => navigate("/newsletters")}
          >
            Cancel
          </Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isSaving
              ? "Saving..."
              : isEditMode
                ? "Update Newsletter"
                : "Create Newsletter"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewsletterFormPage;
